import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { resourceSaga } from './saga';
import {
  IAvailableResource,
  IGetHarvestByCultureAndCompanyPayload,
  IGetHarvestPayload,
  IGetResourcesByTaskCategoryIdPayload,
  IGetUsedResourceDistributionPayload,
  IHarvestByCultureAndCompanyListItem,
  IHarvestListItem,
  IResource,
  IResourceDistributionListItem,
  IResourceType,
  ISubmitUsedResourcesUpdatesPayload,
  ResourceState,
} from './types';

export const initialState: ResourceState = {
  harvest: {
    loading: false,
    harvestArr: [],
    selectedCultureId: null,
  },
  harvestByCultureAndCompany: {
    loading: false,
    harvestByCultureAndCompanyArr: [],
  },
  resourceTypes: {
    loading: false,
    typesArr: [],
    selectedResourceTypeId: null,
  },

  resources: {
    loading: false,
    resourcesArr: [],
    selectedResourceId: null,
    availableResourcesArr: [],
  },

  resourceAllocation: {
    changesSubmitLoading: false,
    selectedTaskCategoryId: null,
    selectedUsedResourceId: null,
    resourcesByTaskCategory: {
      resourcesArr: [],
      loading: false,
    },
    resourceDistribution: {
      resourcesArr: [],
      loading: false,
    },
  },
};

const slice = createSlice({
  name: 'resource',
  initialState,
  reducers: {
    getHarvest(state, action: PayloadAction<IGetHarvestPayload>) {},
    setHarvest(state, action: PayloadAction<IHarvestListItem[]>) {
      state.harvest.harvestArr = action.payload;
    },
    setHarvestLoading(state, action: PayloadAction<boolean>) {
      state.harvest.loading = action.payload;
    },
    setHarvestSelectedCultureId(state, action: PayloadAction<number | null>) {
      state.harvest.selectedCultureId = action.payload;
    },

    getHarvestByCultureAndCompany(
      state,
      action: PayloadAction<IGetHarvestByCultureAndCompanyPayload>,
    ) {},
    setHarvestByCultureAndCompanyLoading(
      state,
      action: PayloadAction<boolean>,
    ) {
      state.harvestByCultureAndCompany.loading = action.payload;
    },
    setHarvestByCultureAndCompany(
      state,
      action: PayloadAction<IHarvestByCultureAndCompanyListItem[]>,
    ) {
      state.harvestByCultureAndCompany.harvestByCultureAndCompanyArr =
        action.payload;
    },

    submitUsedResourcesUpdates(
      state,
      action: PayloadAction<ISubmitUsedResourcesUpdatesPayload>,
    ) {},
    setUsedResourcesUpdatesSubmitLoading(
      state,
      action: PayloadAction<boolean>,
    ) {
      state.resourceAllocation.changesSubmitLoading = action.payload;
    },

    setSelectedUsedResourceId(state, action: PayloadAction<number | null>) {
      state.resourceAllocation.selectedUsedResourceId = action.payload;
    },

    getUsedResourceDistribution(
      state,
      action: PayloadAction<IGetUsedResourceDistributionPayload>,
    ) {},
    setUsedResourceDistribution(
      state,
      action: PayloadAction<IResourceDistributionListItem[]>,
    ) {
      state.resourceAllocation.resourceDistribution.resourcesArr =
        action.payload;
    },
    setUsedResourceDistributionLoading(state, action: PayloadAction<boolean>) {
      state.resourceAllocation.resourceDistribution.loading = action.payload;
    },

    getResourcesByTaskCategoryId(
      state,
      action: PayloadAction<IGetResourcesByTaskCategoryIdPayload>,
    ) {},
    setResourcesByTaskCategoryId(state, action: PayloadAction<any[]>) {
      state.resourceAllocation.resourcesByTaskCategory.resourcesArr =
        action.payload;
    },
    setResourcesByTaskCategoryIdLoading(state, action: PayloadAction<boolean>) {
      state.resourceAllocation.resourcesByTaskCategory.loading = action.payload;
    },

    loadResourceTypes(state) {},
    setResourceTypes(state, action: PayloadAction<IResourceType[]>) {
      state.resourceTypes.typesArr = action.payload;
    },
    setResourceTypesLoading(state, action: PayloadAction<boolean>) {
      state.resourceTypes.loading = action.payload;
    },
    setSelectedResourceTypeId(state, action: PayloadAction<number>) {
      state.resourceTypes.selectedResourceTypeId = action.payload;
    },

    setResources(state, action: PayloadAction<IResource[]>) {
      state.resources.resourcesArr = action.payload;
    },
    setAvailableResources(state, action: PayloadAction<IAvailableResource[]>) {
      state.resources.availableResourcesArr = action.payload;
    },
    setResourcesLoading(state, action: PayloadAction<boolean>) {
      state.resources.loading = action.payload;
    },
    setSelectedResourceId(state, action: PayloadAction<number | null>) {
      state.resources.selectedResourceId = action.payload;
    },

    setSelectedTaskCategoryId(state, action: PayloadAction<number | null>) {
      state.resourceAllocation.selectedTaskCategoryId = action.payload;
    },
  },
});

export const { actions: resourceActions } = slice;

export const useResourceSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: resourceSaga });
  return { actions: slice.actions };
};

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = useResourceSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.someAction());
 *   };
 * }
 */
